/**
 * <InlineCard /> — renders a `render: "card"` tool result inside the
 * chat panel (M22).
 *
 * Used by the explain tools (rule citation, audit trace, override
 * stack, workflow decision) and the count_employees / query_balances
 * tools. Shows the summary prominently plus a compact key/value list
 * of the result's scalar fields. Nested structures (the full engine
 * trace, the resolution path, the approval steps) are handed to the
 * model as tool-result text — it weaves them into its reply — so the
 * card stays a tight visual companion rather than a data dump.
 */

const _SKIP_KEYS = new Set(["trace", "components", "steps", "path"]);

function _label(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function InlineCard({
  summary,
  data,
}: {
  summary: string;
  data: Record<string, unknown>;
}) {
  const scalarEntries = Object.entries(data).filter(
    ([k, v]) =>
      !_SKIP_KEYS.has(k) &&
      (typeof v === "string" ||
        typeof v === "number" ||
        typeof v === "boolean"),
  );

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-default p-2.5">
      <p className="text-xs text-text-primary">{summary}</p>
      {scalarEntries.length > 0 && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 border-t border-border-subtle pt-2 text-xs">
          {scalarEntries.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-text-tertiary">{_label(key)}</dt>
              <dd className="truncate text-text-secondary">
                {String(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
